import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { CreditCard, Truck, CheckCircle, Shield, ChevronLeft, MapPin, Zap, Edit2, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useCart, useAuth, API } from '../App';
import { toast } from 'sonner';
import SubsidyForm from '../components/SubsidyForm';

const CheckoutPage = () => {
    const navigate = useNavigate();
    const { cart, cartTotal, clearCart } = useCart();
    const { user, token } = useAuth();
    const [loading, setLoading] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [editingSection, setEditingSection] = useState(null);
    const [form, setForm] = useState({
        address: '', city: '', state: '', pincode: '', phone: '',
        payment_method: 'razorpay',
        subsidy_details: {}
    });
    const [claimSubsidy, setClaimSubsidy] = useState(false);
    const [orderPlaced, setOrderPlaced] = useState(false);

    // Load user profile data on mount
    useEffect(() => {
        const savedProfile = localStorage.getItem('user_profile_data');
        if (savedProfile) {
            try {
                const profile = JSON.parse(savedProfile);
                setUserProfile(profile);

                // Pre-fill form with saved data
                if (profile.address) {
                    setForm(prev => ({
                        ...prev,
                        address: profile.address.street || '',
                        city: profile.address.city || '',
                        state: profile.address.state || '',
                        pincode: profile.address.pincode || '',
                        phone: profile.phone || ''
                    }));
                }
            } catch (e) {
                console.error('Failed to load user profile:', e);
            }
        }
    }, []);

    // Check if any product in cart is eligible for subsidy
    const isSubsidyEligible = cart.some(item => item.subsidy_eligible);

    const handlePlaceOrder = async () => {
        if (!form.address || !form.city || !form.state || !form.pincode || !form.phone) {
            toast.error('Please fill all address fields');
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/api/orders`, {
                items: cart.map(item => ({ product_id: item.id, quantity: item.quantity, name: item.name, price: item.price, image_url: item.image_url })),
                shipping_address: {
                    name: user?.name || '',
                    address: form.address,
                    city: form.city,
                    state: form.state,
                    pincode: form.pincode,
                    phone: form.phone
                },
                payment_method: form.payment_method,
                claim_subsidy: claimSubsidy,
                subsidy_details: claimSubsidy ? form.subsidy_details : null
            }, { headers: { Authorization: `Bearer ${token}` } });
            setOrderPlaced(true);
            clearCart();
        } catch (error) {
            toast.error('Failed to place order');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveEdit = (section) => {
        // Update localStorage with edited data
        const updatedProfile = {
            ...userProfile,
            address: {
                street: form.address,
                city: form.city,
                state: form.state,
                pincode: form.pincode
            },
            phone: form.phone
        };

        localStorage.setItem('user_profile_data', JSON.stringify(updatedProfile));
        setUserProfile(updatedProfile);
        setEditingSection(null);
        toast.success('Profile updated successfully');
    };

    if (orderPlaced) {
        return (
            <div className="min-h-screen md:pt-20 flex items-center justify-center -mt-14 pt-14 md:mt-0">
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-md mx-auto p-8">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: 'spring' }} className="w-20 h-20 mx-auto bg-accent/10 rounded-full flex items-center justify-center mb-6">
                        <CheckCircle className="w-10 h-10 text-accent" />
                    </motion.div>
                    <h1 className="text-2xl font-bold mb-2">Order Placed Successfully!</h1>
                    <p className="text-muted-foreground mb-6">Thank you for your order. We'll send you a confirmation email shortly.</p>
                    <div className="space-y-3">
                        <Button className="w-full btn-primary" onClick={() => navigate('/dashboard')}>View My Orders</Button>
                        <Button variant="outline" className="w-full" onClick={() => navigate('/shop')}>Continue Shopping</Button>
                    </div>
                </motion.div>
            </div>
        );
    }

    if (cart.length === 0) {
        navigate('/cart');
        return null;
    }

    const total = Math.round(cartTotal * 1.18);

    return (
        <div className="min-h-screen md:pt-20 bg-secondary/30 pb-24 md:pb-0">
            <div className="container-solar px-4 md:px-6 -mt-14 pt-14 md:mt-0 md:pt-8">
                <Link to="/cart" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6"><ChevronLeft className="w-4 h-4" />Back to Cart</Link>
                <h1 className="text-2xl font-bold mb-8">Checkout</h1>

                <div className="grid lg:grid-cols-3 gap-4 md:gap-8">
                    <div className="lg:col-span-2 space-y-6">
                        {/* User Profile Summary */}
                        {userProfile && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border border-primary/20 p-4 md:p-6"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/20 rounded-full flex items-center justify-center">
                                            <User className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-sm md:text-base">{userProfile.name}</h3>
                                            <p className="text-xs md:text-sm text-muted-foreground">{userProfile.email}</p>
                                        </div>
                                    </div>
                                    {editingSection !== 'profile' && (
                                        <Button variant="ghost" size="sm" onClick={() => setEditingSection('profile')} className="text-primary text-xs">
                                            <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                                        </Button>
                                    )}
                                </div>

                                <AnimatePresence mode="wait">
                                    {editingSection === 'profile' ? (
                                        <motion.div key="edit-profile" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3 pt-3 border-t border-primary/20">
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="space-y-1">
                                                    <Label className="text-xs">Monthly Bill (₹)</Label>
                                                    <Input
                                                        type="number"
                                                        value={form.monthlyBill || userProfile.solarInfo?.monthlyElectricityBill || ''}
                                                        onChange={e => setForm({ ...form, monthlyBill: e.target.value })}
                                                        placeholder="e.g. 3000"
                                                        className="text-sm h-9"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs">Home Space (sq ft)</Label>
                                                    <Input
                                                        type="number"
                                                        value={form.homeSpace || userProfile.solarInfo?.homeSpaceSqFt || ''}
                                                        onChange={e => setForm({ ...form, homeSpace: e.target.value })}
                                                        placeholder="e.g. 1000"
                                                        className="text-sm h-9"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs">Roof Type</Label>
                                                    <select
                                                        value={form.roofType || userProfile.solarInfo?.roofType || 'flat'}
                                                        onChange={e => setForm({ ...form, roofType: e.target.value })}
                                                        className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background"
                                                    >
                                                        <option value="flat">Flat</option>
                                                        <option value="sloped">Sloped</option>
                                                        <option value="mixed">Mixed</option>
                                                    </select>
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs">Property Type</Label>
                                                    <select
                                                        value={form.propertyType || userProfile.solarInfo?.propertyType || 'residential'}
                                                        onChange={e => setForm({ ...form, propertyType: e.target.value })}
                                                        className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background"
                                                    >
                                                        <option value="residential">Residential</option>
                                                        <option value="commercial">Commercial</option>
                                                        <option value="industrial">Industrial</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button size="sm" className="btn-primary text-xs" onClick={() => {
                                                    const updatedProfile = {
                                                        ...userProfile,
                                                        solarInfo: {
                                                            ...userProfile.solarInfo,
                                                            monthlyElectricityBill: form.monthlyBill || userProfile.solarInfo?.monthlyElectricityBill,
                                                            homeSpaceSqFt: form.homeSpace || userProfile.solarInfo?.homeSpaceSqFt,
                                                            roofType: form.roofType || userProfile.solarInfo?.roofType,
                                                            propertyType: form.propertyType || userProfile.solarInfo?.propertyType,
                                                        }
                                                    };
                                                    localStorage.setItem('user_profile_data', JSON.stringify(updatedProfile));
                                                    setUserProfile(updatedProfile);
                                                    setEditingSection(null);
                                                    toast.success('Profile updated!');
                                                }}>Save</Button>
                                                <Button size="sm" variant="outline" className="text-xs" onClick={() => setEditingSection(null)}>Cancel</Button>
                                            </div>
                                        </motion.div>
                                    ) : userProfile.solarInfo ? (
                                        <motion.div key="display-profile" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 pt-3 md:pt-4 border-t border-primary/20">
                                            <div>
                                                <p className="text-[10px] md:text-xs text-muted-foreground">Monthly Bill</p>
                                                <p className="font-medium text-sm">₹{userProfile.solarInfo.monthlyElectricityBill}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] md:text-xs text-muted-foreground">Home Space</p>
                                                <p className="font-medium text-sm">{userProfile.solarInfo.homeSpaceSqFt} sq ft</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] md:text-xs text-muted-foreground">Roof Type</p>
                                                <p className="font-medium text-sm capitalize">{userProfile.solarInfo.roofType}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] md:text-xs text-muted-foreground">Property</p>
                                                <p className="font-medium text-sm capitalize">{userProfile.solarInfo.propertyType}</p>
                                            </div>
                                        </motion.div>
                                    ) : null}
                                </AnimatePresence>
                            </motion.div>
                        )}

                        {/* Shipping Address */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-card rounded-xl border p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="font-semibold text-lg flex items-center gap-2">
                                    <Truck className="w-5 h-5 text-primary" />
                                    Shipping Address
                                </h2>
                                {userProfile && !editingSection && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setEditingSection('address')}
                                        className="text-primary"
                                    >
                                        <Edit2 className="w-4 h-4 mr-2" />
                                        Edit
                                    </Button>
                                )}
                            </div>

                            <AnimatePresence mode="wait">
                                {editingSection === 'address' || !userProfile ? (
                                    <motion.div
                                        key="edit"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className="grid gap-4"
                                    >
                                        <div className="space-y-2">
                                            <Label>Full Address</Label>
                                            <Input
                                                value={form.address}
                                                onChange={e => setForm({ ...form, address: e.target.value })}
                                                placeholder="House/Flat No, Street, Landmark"
                                            />
                                        </div>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            <div className="space-y-2"><Label>City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
                                            <div className="space-y-2"><Label>State</Label><Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} /></div>
                                        </div>
                                        <div className="grid md:grid-cols-2 gap-4">
                                            <div className="space-y-2"><Label>PIN Code</Label><Input value={form.pincode} onChange={e => setForm({ ...form, pincode: e.target.value })} /></div>
                                            <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
                                        </div>
                                        {editingSection === 'address' && (
                                            <div className="flex gap-2 mt-2">
                                                <Button onClick={() => handleSaveEdit('address')} className="btn-primary">Save Changes</Button>
                                                <Button variant="outline" onClick={() => setEditingSection(null)}>Cancel</Button>
                                            </div>
                                        )}
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="display"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className="bg-muted/50 rounded-lg p-4"
                                    >
                                        <div className="flex items-start gap-3">
                                            <MapPin className="w-5 h-5 text-primary mt-0.5" />
                                            <div>
                                                <p className="font-medium">{form.address}</p>
                                                <p className="text-sm text-muted-foreground mt-1">
                                                    {form.city}, {form.state} - {form.pincode}
                                                </p>
                                                <p className="text-sm text-muted-foreground mt-1">
                                                    Phone: {form.phone}
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>

                        {/* Subsidy Form */}
                        {isSubsidyEligible && (
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                                <SubsidyForm
                                    formData={form}
                                    setFormData={setForm}
                                    claimSubsidy={claimSubsidy}
                                    setClaimSubsidy={setClaimSubsidy}
                                />
                            </motion.div>
                        )}

                        {/* Payment Method */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card rounded-xl border p-4 md:p-6">
                            <h2 className="font-semibold text-lg mb-6 flex items-center gap-2">
                                <CreditCard className="w-5 h-5 text-primary" />
                                Payment Method
                            </h2>
                            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                                        <CreditCard className="w-6 h-6 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium">Razorpay</p>
                                        <p className="text-sm text-muted-foreground">Secure online payment gateway</p>
                                    </div>
                                    <CheckCircle className="w-5 h-5 text-primary" />
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground mt-3">
                                💳 We accept all major credit/debit cards, UPI, and net banking through Razorpay
                            </p>
                        </motion.div>
                    </div>

                    {/* Order Summary */}
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="lg:col-span-1">
                        <div className="bg-card rounded-xl border p-4 md:p-6 sticky top-24">
                            <h2 className="font-semibold text-lg mb-6">Order Summary</h2>
                            <div className="space-y-3 max-h-48 overflow-y-auto mb-4">
                                {cart.map(item => (
                                    <div key={item.id} className="flex items-center gap-3 text-sm">
                                        <img src={item.image_url || 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=100'} alt={item.name} className="w-12 h-12 object-cover rounded" />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">{item.name}</p>
                                            <p className="text-muted-foreground">Qty: {item.quantity}</p>
                                        </div>
                                        <p className="font-medium">₹{(item.price * item.quantity).toLocaleString()}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="border-t pt-4 space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>₹{cartTotal.toLocaleString()}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span className="text-accent">Free</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">GST (18%)</span><span>₹{Math.round(cartTotal * 0.18).toLocaleString()}</span></div>
                            </div>
                            <div className="border-t my-4" />
                            <div className="flex justify-between text-lg font-bold mb-6"><span>Total</span><span>₹{total.toLocaleString()}</span></div>
                            <Button onClick={handlePlaceOrder} disabled={loading} className="w-full btn-primary py-6">
                                {loading ? 'Placing Order...' : 'Place Order'}
                            </Button>
                            <div className="flex items-center justify-center gap-2 mt-4 text-xs text-muted-foreground">
                                <Shield className="w-4 h-4" />
                                Secure checkout
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;
